import { Divider, Stack, Typography } from "@mui/material";
import { ReuseEllipsisTooltip } from "../ui";
import { RecommendedAccount } from "../../types/apiTypes";
import { GeoIcon, IndustryIcon, RevenueIcon } from "../images";
import { RecommendedReasonHeader } from "./RecommendedReasonHeader";
import { RecommendedAccountActionBlock } from "./RecommendedAccountActionBlock";
import { TwoPersonIcon } from "../images/TwoPersonIcon";
import { RecommendedCardDetails } from "./RecommendedCardDetails";

type Props = {
  recommendedAccount: RecommendedAccount;
  isDemo: boolean;
};

export const RecommendedAccountCard = ({
  recommendedAccount,
  isDemo,
}: Props) => {
  const { id, reason, size, region, name, revenue, industry } =
    recommendedAccount;

  return (
    <Stack
      sx={{
        border: "1px solid #DEDEDE",
        borderRadius: "8px",
        width: "100%",
        maxWidth: "calc(25% - 8px)",
        minWidth: 0,
      }}
    >
      <Stack
        sx={{
          flexGrow: 1,
          gap: "16px",
          paddingY: "16px",
          width: "100%",
        }}
      >
        <Stack gap={"8px"}>
          <Stack
            sx={{
              flexDirection: "row",
              alignItems: "flex-start",
              justifyContent: "space-between",
              paddingX: "16px",
            }}
          >
            <Typography
              style={{
                fontSize: "16px",
                lineHeight: "22px",
                fontWeight: 600,
                color: "#232323",
              }}
            >
              {name}
            </Typography>
            {!isDemo && (
              <RecommendedCardDetails recommendedAccount={recommendedAccount} />
            )}
          </Stack>

          <Stack
            flexDirection={"row"}
            gap={"8px"}
            alignItems={"center"}
            sx={{
              padding: "0 16px 0 12px",
              maxWidth: "100%",
            }}
          >
            <Stack
              sx={{
                width: "22px",
                height: "22px",
                flexShrink: 0,
                border: "1px solid #DEDEDE",
                borderRadius: "50%",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <GeoIcon />
            </Stack>
            <ReuseEllipsisTooltip
              tooltip={region}
              children={region}
              style={{
                fontSize: "14px",
                lineHeight: "22px",
                fontWeight: 400,
                color: "#464646",
              }}
            />
          </Stack>
        </Stack>

        <Stack gap={"12px"} paddingX={"16px"}>
          <Stack flexDirection={"row"} gap={"8px"} alignItems={"center"}>
            <TwoPersonIcon
              color={"#797979"}
              secondColor={"#797979"}
              width={"14px"}
              height={"14px"}
            />
            <ReuseEllipsisTooltip
              tooltip={`${size} Employees`}
              children={
                <>
                  {size}
                  {size !== "--" && (
                    <span style={{ fontWeight: 400, verticalAlign: "center" }}>
                      Employees
                    </span>
                  )}
                </>
              }
              style={{
                fontSize: "12px",
                lineHeight: "12px",
                fontWeight: 500,
                color: "#232323",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            />
          </Stack>
          <Stack flexDirection={"row"} gap={"8px"} alignItems={"center"}>
            <IndustryIcon />
            <ReuseEllipsisTooltip
              tooltip={industry}
              children={industry}
              style={{
                fontSize: "12px",
                lineHeight: "12px",
                fontWeight: 500,
                color: "#232323",
              }}
            />
          </Stack>
          <Stack flexDirection={"row"} gap={"8px"} alignItems={"center"}>
            <RevenueIcon />
            <ReuseEllipsisTooltip
              tooltip={revenue || "--"}
              children={revenue || "--"}
              style={{
                fontSize: "12px",
                lineHeight: "12px",
                fontWeight: 500,
                color: "#232323",
              }}
            />
          </Stack>
        </Stack>
      </Stack>
      <Divider />
      <Stack gap={"20px"} sx={{ padding: "16px" }}>
        <Stack gap={"12px"}>
          <RecommendedReasonHeader />
          <Typography
            variant="body1"
            sx={{
              color: "#464646",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {reason}
          </Typography>
        </Stack>
        <RecommendedAccountActionBlock ids={[id]} isDemo={isDemo} />
      </Stack>
    </Stack>
  );
};
